/**
 * Utility functions for downloading reports
 */

import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { PDFDocument, rgb, StandardFonts } from "pdf-lib";

const backgroundImage = "/images/learnerreportpdf.webp";

/**
 * Downloads data as CSV file
 * @param {Array} data - Array of objects to be converted to CSV
 * @param {String} filename - Name of the file to be downloaded (optional, defaults to 'report.csv')
 */
export const downloadCsv = (data, filename = 'report.csv') => {
    if (!data || !Array.isArray(data) || data.length === 0) {
        console.error('No data provided for CSV download');
        return;
    }

    // Get headers from the first object
    const headers = Object.keys(data[0]);
    
    // Create CSV content
    const csvContent = [
        // Headers row
        headers.join(','),
        // Data rows
        ...data.map(row => 
            headers.map(header => {
                const value = row[header];
                // Handle null/undefined values
                if (value === null || value === undefined) {
                    return '';
                }
                // Convert to string and escape commas and quotes
                const stringValue = String(value);
                // If value contains comma, quote, or newline, wrap in quotes and escape quotes
                if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
                    return `"${stringValue.replace(/"/g, '""')}"`;
                }
                return stringValue;
            }).join(',')
        )
    ].join('\n');

    // Create and download the file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    
    if (link.download !== undefined) {
        // Create object URL
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', filename);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        // Clean up the object URL
        URL.revokeObjectURL(url);
    } else {
        // Fallback for older browsers
        console.error('CSV download not supported in this browser');
    }
};

/**
 * Downloads data as CSV file with custom headers mapping
 * @param {Array} data - Array of objects to be converted to CSV
 * @param {Object} headerMapping - Object mapping data keys to display headers
 * @param {String} filename - Name of the file to be downloaded (optional, defaults to 'report.csv')
 */
export const downloadCsvWithCustomHeaders = (data, headerMapping, filename = 'report.csv') => {
    if (!data || !Array.isArray(data) || data.length === 0) {
        console.error('No data provided for CSV download');
        return;
    }

    if (!headerMapping || typeof headerMapping !== 'object') {
        console.error('Header mapping is required');
        return;
    }

    // Get headers from the mapping
    const headers = Object.keys(headerMapping);
    const dataKeys = Object.values(headerMapping);
    
    // Create CSV content
    const csvContent = [
        // Headers row
        headers.join(','),
        // Data rows
        ...data.map(row => 
            dataKeys.map(key => {
                const value = row[key];
                // Handle null/undefined values
                if (value === null || value === undefined) {
                    return '';
                }
                // Convert to string and escape commas and quotes
                const stringValue = String(value);
                // If value contains comma, quote, or newline, wrap in quotes and escape quotes
                if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
                    return `"${stringValue.replace(/"/g, '""')}"`;
                }
                return stringValue;
            }).join(',')
        )
    ].join('\n');

    // Create and download the file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    
    if (link.download !== undefined) {
        // Create object URL
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', filename);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        // Clean up the object URL
        URL.revokeObjectURL(url);
    } else {
        // Fallback for older browsers
        console.error('CSV download not supported in this browser');
    }
};

/**
 * Downloads learner report as CSV with formatted headers
 * @param {Array} learnerData - Array of learner objects
 * @param {String} cohortName - Name of the cohort for filename
 */
export const downloadLearnerReportCsv = (learnerData, cohortName = '') => {
    if (!learnerData || !Array.isArray(learnerData) || learnerData.length === 0) {
        console.error('No learner data provided for CSV download');
        return;
    }

    // Custom header mapping for learner report
    const headerMapping = {
        'Unique ID': 'uid',
        'Name': 'name',
        'Email ID': 'email',
        'Modules Completed': 'completed_modules',
        'Tests Completed': 'completed_tests',
        'Average Score': 'avg_po_score',
        'Time Spent (Hours)': 'hour',
        'Time Spent (Minutes)': 'min',
        'Status': 'status',
        'Cohort Name': 'cohort_name'
    };

    // Format the data for CSV
    const formattedData = learnerData.map(learner => ({
        ...learner,
        // Format time spent as readable text
        'Time Spent': `${learner.hour || 0} Hour ${learner.min || 0} Min`
    }));

    // Generate filename with timestamp
    const timestamp = new Date().toISOString().split('T')[0];
    const filename = `cohort-report-${cohortName ? cohortName.replace(/[^a-zA-Z0-9]/g, '-') : 'all'}-${timestamp}.csv`;

    downloadCsvWithCustomHeaders(formattedData, headerMapping, filename);
};

/**
 * Downloads user report as PDF with background image
 * @param {Object} userData - User data object
 * @param {Array} courseData - Array of course data
 * @param {Object} courseReport - Course summary data
 * @param {Number} avgPoScore - Average post-learning score
 * @param {String} certificates - Certificates earned
 * @param {String} filename - Name of the file to be downloaded (optional)
 */
export const downloadUserReportPdf = async (userData, courseData, courseReport, avgPoScore, certificates, filename = '') => {
    try {
        // Create new PDF document
        const pdf = new jsPDF('p', 'mm', 'a4');
        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();
        
        // Load background image
        const img = new Image();
        img.crossOrigin = 'anonymous';
        
        return new Promise((resolve, reject) => {
            img.onload = () => {
                try {
                    // Compute cover-fit background placement to avoid cropping/stretching
                    // Add background image (cover-fit to page)
                    const imgAspect = img.width / img.height;
                    const a4Aspect = pageWidth / pageHeight;
                    let imgW = pageWidth, imgH = pageHeight, imgX = 0, imgY = 0;
                    if (imgAspect > a4Aspect) {
                      // Image is wider than A4: fit height, center horizontally
                      imgH = pageHeight;
                      imgW = pageHeight * imgAspect;
                      imgX = -(imgW - pageWidth) / 2;
                    } else {
                      // Image is taller: fit width, center vertically
                      imgW = pageWidth;
                      imgH = pageWidth / imgAspect;
                      imgY = -(imgH - pageHeight) / 2 + 5; // move image LOWER by 5mm for more top padding
                    }
                    pdf.addImage(img, 'WEBP', imgX, imgY, imgW, imgH);
                    // Use a dark text color so it is visible on the white center
                    pdf.setTextColor(33, 37, 59); // dark slate
                    
                    // Minimal top band to soften inner border only
                    // pdf.setFillColor(255, 255, 255);
                    // pdf.rect(15, 20, pageWidth - 30, 35, 'F');

                    const left = 30; // safe left margin inside border
                    const right = pageWidth - 30; // safe right margin
                    const bottom = pageHeight - 40; // reserve bottom area (logo)
                    let y = 85; // start below the template heading

                    // Estimate total height and compute scale to fit one page
                    const summaryLines = 6; // summary + 5 lines
                    const tableRows = (courseData && courseData.length) ? courseData.length : 0;
                    const certificatesTextRaw = certificates || 'No certificates earned';
                    const tempSplit = pdf.splitTextToSize(certificatesTextRaw, right - left);
                    const certificateLines = tempSplit.length;
                    const estimatedLineHeight = 6;
                    const availableHeight = bottom - y;
                    const estimatedNeed = (2 + summaryLines + 2 + certificateLines + 3 + tableRows) * estimatedLineHeight + 70;
                    const scale = Math.max(0.65, Math.min(1, availableHeight / Math.max(estimatedNeed, 1)));
                    const headingSize = Math.max(10, Math.floor(14 * scale));
                    const labelSize = Math.max(9, Math.floor(12 * scale));
                    const bodySize = Math.max(8, Math.floor(10 * scale));
                    const tableHeaderSize = Math.max(8, Math.floor(10 * scale));
                    const tableBodySize = Math.max(7, Math.floor(9 * scale));
                    const gap = Math.max(4, Math.floor(6 * scale));
                    
                    // Add user information
                    pdf.setFontSize(labelSize);
                    pdf.setFont('helvetica', 'bold');
                    pdf.text(userData.name || 'N/A', left, y);
                    
                    pdf.setFontSize(bodySize);
                    pdf.setFont('helvetica', 'normal');
                    y += gap;
                    pdf.text(`Cohort: ${userData.cohort_name || 'N/A'}`, left, y);
                    
                    // Add summary section
                    pdf.setFontSize(headingSize);
                    pdf.setFont('helvetica', 'bold');
                    y += gap + 6;
                    pdf.text('Summary', left, y);
                    
                    pdf.setFontSize(bodySize);
                    pdf.setFont('helvetica', 'normal');
                    y += gap; pdf.text(`Average Post-Learning Score: ${Math.ceil(avgPoScore)}%`, left, y);
                    y += gap; pdf.text(`Courses Completed: ${courseReport.course_completed}`, left, y);
                    y += gap; pdf.text(`Modules Completed: ${courseReport.modules_completed}`, left, y);
                    y += gap; pdf.text(`Tests Completed: ${courseReport.tests_completed}`, left, y);
                    y += gap; pdf.text(`Total Time Spent: ${courseReport.min_spent} minutes`, left, y);
                    
                    // Add certificates section
                    pdf.setFontSize(headingSize);
                    pdf.setFont('helvetica', 'bold');
                    y += gap + 6; pdf.text('Certificates Earned', left, y);
                    
                    pdf.setFontSize(Math.max(7, Math.floor(9 * scale)));
                    pdf.setFont('helvetica', 'normal');
                    const certificatesText = certificatesTextRaw;
                    const splitCertificates = tempSplit;
                    y += gap;
                    const certHeight = splitCertificates.length * 5;
                    // Do not add pages; clamp by reducing scale above
                    pdf.text(splitCertificates, left, y);
                    
                    // Add course details table
                    if (courseData && courseData.length > 0) {
                        let yPosition = y + 20;

                        const drawHeader = () => {
                            pdf.setFontSize(headingSize);
                            pdf.setFont('helvetica', 'bold');
                            pdf.text('Course Details', left, yPosition);
                            yPosition += 10;
                            pdf.setFontSize(tableHeaderSize);
                            pdf.setFont('helvetica', 'bold');
                            const colName = left;
                            const colModules = left + 60; // tighter spacing
                            const colPre = left + 90;     // tighter spacing
                            const colPost = left + 120;   // tighter spacing
                            const colTime = Math.min(left + 150, right - 10); // keep inside frame
                            pdf.text('Course Name', colName, yPosition);
                            pdf.text('Modules', colModules, yPosition);
                            pdf.text('Pre-Tests', colPre, yPosition);
                            pdf.text('Post-Tests', colPost, yPosition);
                            pdf.text('Time (min)', colTime, yPosition);
                            yPosition += Math.max(6, gap);
                            return { colName, colModules, colPre, colPost, colTime };
                        };

                        let cols = drawHeader();
                        pdf.setFont('helvetica', 'normal');
                        pdf.setFontSize(tableBodySize);

                        courseData.forEach((course) => {
                            // Do not add pages; clamp content by using smaller fonts above
                            if (yPosition > bottom) return;

                            pdf.text(course.course_name || 'N/A', cols.colName, yPosition);
                            pdf.text(`${course.modules_completed}/${course.num_modules}`, cols.colModules, yPosition);
                            pdf.text(String(course.pr_completed), cols.colPre, yPosition);
                            pdf.text(String(course.po_completed), cols.colPost, yPosition);
                            pdf.text(String(course.mins_spent), cols.colTime, yPosition);
                            yPosition += Math.max(6, gap);
                        });
                    }
                    
                    // Generate filename if not provided
                    if (!filename) {
                        const timestamp = new Date().toISOString().split('T')[0];
                        const userName = userData.name ? userData.name.replace(/[^a-zA-Z0-9]/g, '-') : 'user';
                        filename = `user-report-${userName}-${timestamp}.pdf`;
                    }
                    
                    // Save the PDF
                    pdf.save(filename);
                    resolve();
                } catch (error) {
                    console.error('Error creating PDF:', error);
                    reject(error);
                }
            };
            
            img.onerror = () => {
                console.error('Error loading background image');
                // Continue without background image
                try {
                    pdf.setTextColor(33, 37, 59);
                    const left = 30;
                    const right = pageWidth - 30;
                    const bottom = pageHeight - 40;
                    let y = 110;
                    
                    // Estimate and scale for one page in no-image branch
                    const summaryLines = 6;
                    const tableRows = (courseData && courseData.length) ? courseData.length : 0;
                    const certificatesTextRaw = certificates || 'No certificates earned';
                    const tempSplit = pdf.splitTextToSize(certificatesTextRaw, right - left);
                    const certificateLines = tempSplit.length;
                    const estimatedLineHeight = 6;
                    const availableHeight = bottom - y;
                    const estimatedNeed = (2 + summaryLines + 2 + certificateLines + 3 + tableRows) * estimatedLineHeight + 70;
                    const scale = Math.max(0.65, Math.min(1, availableHeight / Math.max(estimatedNeed, 1)));
                    const headingSize = Math.max(10, Math.floor(14 * scale));
                    const labelSize = Math.max(9, Math.floor(12 * scale));
                    const bodySize = Math.max(8, Math.floor(10 * scale));
                    const tableHeaderSize = Math.max(8, Math.floor(10 * scale));
                    const tableBodySize = Math.max(7, Math.floor(9 * scale));
                    const gap = Math.max(4, Math.floor(6 * scale));

                    // Add user information
                    pdf.setFontSize(labelSize);
                    pdf.setFont('helvetica', 'bold');
                    pdf.text(userData.name || 'N/A', left, y);
                    
                    pdf.setFontSize(bodySize);
                    pdf.setFont('helvetica', 'normal');
                    y += gap;
                    pdf.text(`Cohort: ${userData.cohort_name || 'N/A'}`, left, y);
                    
                    // Add summary section
                    pdf.setFontSize(headingSize);
                    pdf.setFont('helvetica', 'bold');
                    y += gap + 6; pdf.text('Summary', left, y);
                    
                    pdf.setFontSize(bodySize);
                    pdf.setFont('helvetica', 'normal');
                    y += gap; pdf.text(`Average Post-Learning Score: ${Math.ceil(avgPoScore)}%`, left, y);
                    y += gap; pdf.text(`Courses Completed: ${courseReport.course_completed}`, left, y);
                    y += gap; pdf.text(`Modules Completed: ${courseReport.modules_completed}`, left, y);
                    y += gap; pdf.text(`Tests Completed: ${courseReport.tests_completed}`, left, y);
                    y += gap; pdf.text(`Total Time Spent: ${courseReport.min_spent} minutes`, left, y);
                    
                    // Add certificates section
                    pdf.setFontSize(headingSize);
                    pdf.setFont('helvetica', 'bold');
                    y += gap + 6; pdf.text('Certificates Earned', left, y);
                    
                    pdf.setFontSize(Math.max(7, Math.floor(9 * scale)));
                    pdf.setFont('helvetica', 'normal');
                    const certificatesText = certificatesTextRaw;
                    const splitCertificates = tempSplit;
                    y += gap; pdf.text(splitCertificates, left, y);
                    
                    // Add course details table
                    if (courseData && courseData.length > 0) {
                        let yPosition = y + 20;
                        
                        pdf.setFontSize(headingSize);
                        pdf.setFont('helvetica', 'bold');
                        pdf.text('Course Details', left, yPosition);
                        yPosition += 10;
                        
                        // Table headers
                        pdf.setFontSize(tableHeaderSize);
                        pdf.setFont('helvetica', 'bold');
                        const colName = left;
                        const colModules = left + 60; // tighter spacing
                        const colPre = left + 90;     // tighter spacing
                        const colPost = left + 120;   // tighter spacing
                        const colTime = Math.min(left + 150, right - 10);
                        pdf.text('Course Name', colName, yPosition);
                        pdf.text('Modules', colModules, yPosition);
                        pdf.text('Pre-Tests', colPre, yPosition);
                        pdf.text('Post-Tests', colPost, yPosition);
                        pdf.text('Time (min)', colTime, yPosition);
                        yPosition += Math.max(6, gap);
                        
                        // Table data
                        pdf.setFont('helvetica', 'normal');
                        pdf.setFontSize(tableBodySize);
                        courseData.forEach((course, index) => {
                            // Check if we need a new page
                            if (yPosition > bottom) return;
                            
                            pdf.text(course.course_name || 'N/A', colName, yPosition);
                            pdf.text(`${course.modules_completed}/${course.num_modules}`, colModules, yPosition);
                            pdf.text(String(course.pr_completed), colPre, yPosition);
                            pdf.text(String(course.po_completed), colPost, yPosition);
                            pdf.text(String(course.mins_spent), colTime, yPosition);
                            yPosition += Math.max(6, gap);
                        });
                    }
                    
                    // Generate filename if not provided
                    if (!filename) {
                        const timestamp = new Date().toISOString().split('T')[0];
                        const userName = userData.name ? userData.name.replace(/[^a-zA-Z0-9]/g, '-') : 'user';
                        filename = `user-report-${userName}-${timestamp}.pdf`;
                    }
                    
                    // Save the PDF
                    pdf.save(filename);
                    resolve();
                } catch (error) {
                    console.error('Error creating PDF without background:', error);
                    reject(error);
                }
            };
            
            // Load the background image
            img.src = '/images/user-report-pdf.webp';
        });
    } catch (error) {
        console.error('Error in downloadUserReportPdf:', error);
        throw error;
    }
};


// backgroundImage must be a base64 or imported image
// Example: import backgroundImage from "/images/learnerbg.webp";

export const downloadLearnerReportMatrixPdf = ({
  user = {},
  certifications = [],
  courses = [],
  filename = "learner-report.pdf",
}) => {
  const pdf = new jsPDF({ unit: "pt", format: "a4" });
  const width = pdf.internal.pageSize.getWidth();
  const height = pdf.internal.pageSize.getHeight();

  const margin = 60;
  const indent = margin + 30;
  const tableWidth = width - margin * 2;
  const col1Width = tableWidth * 0.65;
  const col2Width = tableWidth * 0.35;
  const rowHeight = 30;

  // -------------------------------------------------------------
  // BACKGROUND IMAGE (OPTIONAL)
  // -------------------------------------------------------------
  const drawBackground = () => {
    try {
      if (backgroundImage) {
        pdf.addImage(backgroundImage, "WEBP", 0, 0, width, height);
      }
    } catch (e) {
      console.warn("Background error:", e);
    }
  };

  // -------------------------------------------------------------
  // PURPLE GRADIENT HEADER BOX (only for first page)
  // -------------------------------------------------------------
  const drawPurpleHeader = (y = 100, title = "Learner Report") => {
    const boxHeight = 100;
    const gradientStart = [102, 45, 145];
    const gradientEnd = [155, 61, 183];

    // Simulate gradient using small lines
    for (let i = 0; i < boxHeight; i++) {
      const ratio = i / boxHeight;
      const r = gradientStart[0] + ratio * (gradientEnd[0] - gradientStart[0]);
      const g = gradientStart[1] + ratio * (gradientEnd[1] - gradientStart[1]);
      const b = gradientStart[2] + ratio * (gradientEnd[2] - gradientStart[2]);
      pdf.setFillColor(r, g, b);
      pdf.rect(0, y + i, width, 1, "F");
    }

    // Title (white)
    pdf.setTextColor("#fff");
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(28);
    pdf.text(title, width / 2, y + 60, { align: "center" });
  };

  // -------------------------------------------------------------
  // FIRST PAGE (Learner Report)
  // -------------------------------------------------------------
  drawBackground();

  drawPurpleHeader(100, "Learner Report");

  // Learner Details
  pdf.setFontSize(16);
  pdf.setTextColor("#000");
  pdf.setFont("helvetica", "normal");

  pdf.text("Name:", indent, 250);
  pdf.setFont("helvetica", "bold");
  pdf.text(String(user.name), indent + 70, 250);

  
  if (certifications.length > 0 && certifications[0] !== "") {
    pdf.setFont("helvetica", "normal");
    pdf.text("Certifications Earned:", indent, 320);

    let cy = 350;
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(15);
    pdf.setTextColor("#222");
    certifications.forEach((c, i) => {
      pdf.text(`${i + 1}. ${String(c)}`, indent + 20, cy);
      cy += 25;
    });
  }else{
    pdf.setFont("helvetica", "normal");
    pdf.text("Certifications Earned:", indent, 320);
    pdf.setFont("helvetica", "bold");
    pdf.text("No Certifications Earned", indent + 170, 320);
  }

  // Footer
  pdf.setFontSize(10);
  pdf.setFont("helvetica", "italic");
  pdf.setTextColor("#555");
  pdf.text("Generated by Learner Management System", width / 2, height - 40, { align: "center" });

  // -------------------------------------------------------------
  // COURSE DETAIL PAGES (no gradient, purple text only)
  // -------------------------------------------------------------
  courses.forEach((course) => {
    pdf.addPage();
    drawBackground();

    const courseTitle = String(course?.course_name || "");
    const maxTextWidth = width - margin * 2; // safe text width

    const wrappedTitle = pdf.splitTextToSize(courseTitle, maxTextWidth);

    // Just purple text title (no gradient)
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(22);
    pdf.setTextColor("#662D91"); // Purple tone from gradient
    let titleY = 120;

    wrappedTitle.forEach((line) => {
        const lineWidth = pdf.getTextWidth(line);
        const lineX = (width - lineWidth) / 2; // manually center each line
        pdf.text(line, lineX, titleY);
        titleY += 28; // line spacing
    });

    // Table Header
    let startY = titleY + 15;
    pdf.setFillColor(230, 230, 230);
    pdf.rect(margin, startY, tableWidth, rowHeight, "F");

    pdf.setFontSize(14);
    pdf.setFont("helvetica", "bold");
    pdf.setTextColor("#000");
    pdf.text("Module", margin + 10, startY + 20);
    pdf.text("Status", margin + col1Width + 10, startY + 20);

    startY += rowHeight;

    // Table Rows
    pdf.setFont("helvetica", "normal");
    (course.modules || []).forEach((m) => {
      pdf.setDrawColor(180);
      pdf.rect(margin, startY, col1Width, rowHeight);
      pdf.rect(margin + col1Width, startY, col2Width, rowHeight);
      let status = String(m?.status || "");
      if (status === "NOT_STARTED"){
            status = "Not Yet Started";
      }else if(status === "PENDING"){
            status = "Pending";
      }else if (status === "COMPLETED"){
            status = "Completed";
      }else if (status === "STARTED"){
        status = "In Progress";
      }
      pdf.setFont("helvetica", "bold");
      pdf.text(String(m?.module_name || ""), margin + 10, startY + 20);
      pdf.setFont("helvetica", "normal");
      pdf.text(status, margin + col1Width + 10, startY + 20);
      startY += rowHeight;
    });
    pdf.setDrawColor(180);
    pdf.rect(margin, startY, col1Width, rowHeight);
    pdf.rect(margin + col1Width, startY, col2Width, rowHeight);
    pdf.setFont("helvetica", "bold");
    pdf.text("Post Assessment Score (%)", margin + 10, startY + 20);
    pdf.setFont("helvetica", "normal");
    pdf.text(String(course?.po_score || "0"), margin + col1Width + 10, startY + 20);
    startY += rowHeight;
    pdf.setDrawColor(180);
    pdf.rect(margin, startY, col1Width, rowHeight);
    pdf.rect(margin + col1Width, startY, col2Width, rowHeight);
    pdf.setFont("helvetica", "bold");
    pdf.text("Learning Time (Minutes)", margin + 10, startY + 20);
    pdf.setFont("helvetica", "normal");
    let sec = parseInt((course?.learning_sec || 0) / 60)
    pdf.text(String(sec), margin + col1Width + 10, startY + 20);
    startY += rowHeight;
  });

  // -------------------------------------------------------------
  // SAVE
  // -------------------------------------------------------------
  pdf.save(filename);
};










